import {
  CheckCircle2,
  CircleDashed,
  CircleSlash,
  Clock,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { Pill, type PillTone } from '../ui/Pill';
import type { ProgressStatus, StationProgress } from './meTypes';

export function ProgressCard({ rows }: { rows: StationProgress[] }) {
  // Always show every station so the inductee sees the complete picture
  // (with "N/A" for stations their kit template doesn't cover). The
  // count is hidden for N/A rows because "0 of 0" reads as missing data.
  return (
    <section className="overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-sm">
      <div className="flex items-center gap-2 border-b border-divider/10 px-5 py-3">
        <ShieldCheck className="h-4 w-4 text-accent" />
        <h2 className="text-sm font-semibold text-fg">Your progress</h2>
      </div>
      <ul className="divide-y divide-divider/10">
        {rows.map((r) => (
          <li
            key={r.station}
            className="flex items-center justify-between gap-3 px-5 py-3"
          >
            <div className="flex items-center gap-2 text-sm font-medium">
              <Pill tone="accent" size="sm">
                {r.station}
              </Pill>
              {r.status !== 'not_required' ? (
                <span className="text-fg">
                  {r.issued} of {r.expected}
                </span>
              ) : null}
            </div>
            <StatusPill status={r.status} />
          </li>
        ))}
      </ul>
    </section>
  );
}

const STATUS_STYLE: Record<
  ProgressStatus,
  { label: string; icon: LucideIcon; tone: PillTone }
> = {
  done: { label: 'Done', icon: CheckCircle2, tone: 'success' },
  in_progress: { label: 'In progress', icon: CircleDashed, tone: 'warning' },
  not_started: { label: 'Not started', icon: Clock, tone: 'neutral' },
  not_required: { label: 'N/A', icon: CircleSlash, tone: 'neutral' },
};

function StatusPill({ status }: { status: ProgressStatus }) {
  const s = STATUS_STYLE[status];
  const Icon = s.icon;
  return (
    <Pill tone={s.tone} icon={<Icon className="h-3 w-3" />}>
      {s.label}
    </Pill>
  );
}
